import React, { useMemo } from 'react';

import { parentKicker } from '../../utils/collectionFamily';
import RecordBadge from './RecordBadge';
import { Skeleton } from '../loading';
import { normalizeId } from '../../utils/ids';
import { isItemTag, isPrivateTag, tagPathLabel, tagRecordOf } from '../../utils/tags';

const PRIVATE_NOTE = 'Only you see this';

/**
 * The rail's Pinned section: the personal collection first and fixed, then
 * the starred collections in the person's own order (userFavorites
 * `position` — the ONE order Home's Collections strip and the collections
 * page's Favorites draw too).
 *
 * The rail only LISTS: it is navigation, one click from anywhere, and a
 * grip in it made every hover over a pin a chance to drag it. Reordering is
 * deliberate — the Reorder button on Home's Collections section or on the
 * collections page's Favorites — and the rail follows at once, because all
 * three read FavoritesContext's order.
 *
 * Then the person's pinned TAGS (tags T5), in their Home order: each a plain
 * row like a collection's — the tag's chip and name (an item tag: its item's
 * card chip and title) — opening its tag page. `tags` are Home's resolved tag
 * pins (GET /api/home `tags`), so a tag the person can no longer open is not
 * here, and never named.
 *
 * `loading`: nothing is known yet; draws placeholder rows (§14), not an empty rail.
 */
export default function RailPinned({ personal, collections, tags = [], loading = false, collapsed, pathname }) {
	const rows = useMemo(() => {
		const list = (collections || []).map(collection => ({ id: normalizeId(collection?._id), collection }));
		return personal ? [{ id: normalizeId(personal._id), collection: personal }, ...list] : list;
	}, [personal, collections]);
	const tagRows = useMemo(() => (Array.isArray(tags) ? tags : [])
		.filter(pin => pin?.tag)
		.map(pin => ({ id: normalizeId(pin.tag._id), pin })), [tags]);

	// Nothing to list yet but the answer is on its way: the section's label and
	// two placeholder rows hold its place, so the rail does not jump on arrival.
	if (!rows.length && !tagRows.length) {
		if (!loading) return null;
		return (
			<div className="rail__pinned" aria-busy="true" data-testid="rail-pinned-pending">
				{!collapsed && <div className="micro-label rail__pinned-label">Pinned</div>}
				<div className="rail__pinned-pending" aria-hidden="true">
					<Skeleton variant="line" count={2} />
				</div>
			</div>
		);
	}

	return (
		<div className="rail__pinned">
			{!collapsed && <div className="micro-label rail__pinned-label">Pinned</div>}
			{rows.length ? (
				<ul className="rail__pinned-list" aria-label="Pinned collections">
					{rows.map(({ id, collection }) => {
						const active = pathname.startsWith(`/collections/${id}`);
						// A plain 36px row like the nav items: 21px chip + name (the
						// record's own surface in the rail — no pill). Collapsed: the
						// chip alone, the name as the tooltip.
						return (
							<li key={id} className="rail__pinned-item">
								<RecordBadge
									kind="collection"
									record={collection}
									variant="plain"
									size="xs"
									tileSize={21}
									// A sub-collection carries its parent's name above its own,
									// so a rail with two "Health"s never reads ambiguously.
									kicker={collapsed ? undefined : (parentKicker(collection) || undefined)}
									title={collapsed ? null : collection?.name}
									tooltip={collapsed ? [parentKicker(collection), collection?.name].filter(Boolean).join(' · ') : undefined}
									aria-label={collection?.name}
									to={`/collections/${id}/items`}
									block
									className={`rail__pin${active ? ' rail__pin--active' : ''}`}
								/>
							</li>
						);
					})}
				</ul>
			) : null}
			{tagRows.length ? (
				<ul className="rail__pinned-list rail__pinned-list--tags" aria-label="Pinned tags">
					{tagRows.map(({ id, pin }) => {
						const tag = pin.tag;
						const item = isItemTag(tag);
						const name = item ? (tag.title || 'Untitled item') : (tag.name || 'Tag');
						const active = pathname === `/tags/${id}`;
						// The row is the tag's own surface in the rail, plain like a
						// collection's; a private tag says so on hover.
						const note = isPrivateTag(tag) ? PRIVATE_NOTE : (item ? undefined : tagPathLabel(tag));
						return (
							<li key={`tag-${id}`} className="rail__pinned-item rail__pinned-item--tag">
								<RecordBadge
									kind={item ? 'item' : 'tag'}
									record={item ? (pin.card || null) : tagRecordOf(tag)}
									variant="plain"
									size="xs"
									tileSize={21}
									title={collapsed ? null : name}
									tooltip={collapsed ? [name, note].filter(Boolean).join(' · ') : note}
									aria-label={name}
									to={`/tags/${id}`}
									block
									className={`rail__pin rail__pin--tag${active ? ' rail__pin--active' : ''}`}
								/>
							</li>
						);
					})}
				</ul>
			) : null}
		</div>
	);
}
